import { mapState, mapActions } from 'vuex';
import noop from 'lodash.noop';

import { MiIcon } from '~shared/ui';
import {
  useDatePeriod,
  usePage,
  usePerPage,
  useUser,
  useQuery,
  useModel,
  useGetDataByInterval,
  authStorageService,
  I18nService,
  DeviceService,
} from '~shared/lib';
import { compareQueriesThenLoadData } from '@/utils/router.utils';
import { Hospital } from '@/models/hospital/Hospital.model';
import { Department } from '@/models/hospital/Department.model';
import { GlobalModalClose } from '~widgets/GlobalModalAndDrawer';
import { DATE_FORMAT } from '~shared/config';
import { mergeOrCreateQuery } from '@/utils/http.util';
import { deleteEmptyValueKeys } from '@/utils/object.util';
import { MainLayout } from '~widgets/layouts';
import LayoutContentHeader from '@/components/layouts/assets/LayoutContentHeader/index.vue';
import SelectHospitalStatus from '@/components/hospitals/SelectHospitalStatus/index.vue';
import HospitalsTable from '@/components/hospitals/HospitalsTable/index.vue';
import CreateOrEditHospitalModal from '@/components/hospitals/CreateOrEditHospitalModal/index.vue';
import PatientsSearchSelect from '@/components/patients/PatientsSearchSelect/index.vue';

export default {
  name: 'VHospitals',
  components: {
    HospitalsTable,
    LayoutContentHeader,
    SelectHospitalStatus,
    MainLayout,
    PatientsSearchSelect,
    MiIcon,
  },
  data() {
    return {
      defaultSort: {},
      statistic: {
        loading: false,
        data: { patientAmount: 0 },
      },
    };
  },
  setup: () => ({
    page: usePage(),
    perPage: usePerPage(),
    period: useDatePeriod(),
    status: useQuery({
      field: 'status',
    }),
    department: useModel({
      fieldNames: {
        id: 'department_id',
        name: 'department_name',
      },
    }),
    patient: useUser(),

    Department,
    DATE_FORMAT,

    getDataByInterval: noop,
  }),
  computed: {
    ...mapState({
      items: (state) => state.hospital.data,
      total: (state) => state.hospital.total,
      loading: (state) => state.hospital.loading,
    }),

    currentSort() {
      let order;

      switch (this.defaultSort.order) {
        case 'ascending':
          order = 'asc';
          break;
        case 'descending':
          order = 'desc';
          break;
        default:
          order = null;
      }

      let prop;

      switch (this.defaultSort.prop) {
        case 'chamber.department.title':
          prop = 'sort_department';
          break;
        case 'chamber.number':
          prop = 'sort_chamber';
          break;
        case 'created_at':
          prop = 'sort_created_at';
          break;
        case 'discharged_at':
          prop = 'sort_discharged_at';
          break;
        case 'status':
          prop = 'sort_status';
          break;
        default:
          prop = 'sort_department';
      }

      return {
        prop,
        order,
      };
    },

    queryWatchers() {
      return {
        [this.currentSort.prop]: this.currentSort.order,
        page: this.page.value,
        per_page: this.perPage.value,
        start_at: this.period.value[0],
        end_at: this.period.value[1],
        status: this.status.value,
        user_id: this.patient.value.id,
        department_id: this.department.value.id,
      };
    },

    statisticCard() {
      return {
        label: this.$t('Hospital.Statistic.PatientAmount'),
        value: this.statistic.data.patientAmount,
        icon: 'BOARD',
      };
    },

    exportDataURL() {
      return mergeOrCreateQuery({
        url: Hospital.exportDataURL,
        query: deleteEmptyValueKeys({
          ...this.queryWatchers,
          lang: I18nService.getLocale(),
          token: authStorageService.getAccessToken(),
          device_id: DeviceService.getDeviceId(),
        }), // per_page, page будут игнорироваться на бэке
      });
    },
  },
  watch: {
    queryWatchers: {
      handler(value, oldValue) {
        compareQueriesThenLoadData({
          query: value,
          oldQuery: oldValue,
          fieldsForResetPage: ['start_at', 'status', 'department_id'],
          getData: this.getDataByInterval,
          resetPage: this.page.reset,
        });
      },
      deep: true,
    },
  },

  methods: {
    ...mapActions({
      setData: 'hospital/setData',
      setLoading: 'hospital/setLoading',
      createItem: 'hospital/createItem',
      editItem: 'hospital/editItem',
      deleteItem: 'hospital/deleteItem',
    }),

    async getData() {
      this.setLoading(true);

      const { data } = await Hospital.find(this.queryWatchers);
      this.setData({
        items: data.data,
        total: data.meta.total,
        overwriteDataState: true,
      });

      this.setLoading(false);
    },

    async getStatistic() {
      this.statistic.loading = true;

      const response = await Hospital.getStatisticByPatientAmount();
      this.statistic.data.patientAmount = response?.data.value ?? 0;

      this.statistic.loading = false;
    },

    async getDataAndStatistic() {
      this.getData();
      this.getStatistic();
    },

    async create() {
      const action = await this.$store.dispatch(
        'modalAndDrawer/openModal',
        CreateOrEditHospitalModal
      );
      if (!(action instanceof GlobalModalClose)) this.createItem(action.data.hospital);
    },

    onSortChange({ prop, order }) {
      this.defaultSort = { prop, order };
    },
  },

  mounted() {
    this.getDataByInterval = useGetDataByInterval(this.getDataAndStatistic);
    this.getDataByInterval();
  },
};
